import { Helmet } from 'react-helmet-async';
import { useParams, useNavigate } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { MapPin, Loader2, Star, AlertCircle } from 'lucide-react';
import Layout from '@/components/layout/Layout';
import JsonLd from '@/components/common/JsonLd';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import SectionHeader from '@/components/common/SectionHeader';
import { getLocalBusinessSchema, getServiceSchema, getBreadcrumbSchema } from '@/lib/jsonld-schema';

interface LocationInfo {
  id: string;
  name: string;
  full_name: string;
  slug: string;
  latitude: number;
  longitude: number;
  description: string;
  service_area_description: string;
  region_name: string;
  country?: string;
  created_at?: string;
  updated_at?: string;
}

interface Service {
  id: string;
  name: string;
  description: string;
  price: string;
  image: string;
}

export default function LocationLandingPage() {
  const { location } = useParams<{ location: string }>();
  const navigate = useNavigate();
  const [locationInfo, setLocationInfo] = useState<LocationInfo | null>(null);
  const [services, setServices] = useState<Service[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchLocationAndServices = async () => {
      try {
        setLoading(true);
        setError(null);
        const { getApiUrl } = await import('../config');
        
        // Fetch location data
        if (!location) {
          setError('No location specified');
          setLoading(false);
          return;
        }

        const locationRes = await fetch(getApiUrl(`/public/locations/${location.toLowerCase()}` ));
        
        if (!locationRes.ok) {
          setError('Location not found');
          setLoading(false);
          return;
        }

        const locationData = await locationRes.json();
        
        if (locationData.success && locationData.data) {
          setLocationInfo(locationData.data);
          
          // Now fetch services for this location
          const serviceList: Service[] = [];
          try {
            const categoriesRes = await fetch(getApiUrl('/public/services/categories'));
            const categoriesData = await categoriesRes.json();
            
            if (categoriesData.success && categoriesData.data) {
              // Get sample services from categories
              for (const category of categoriesData.data) {
                const servicesRes = await fetch(
                  getApiUrl(`/public/categories/${category.id}/services`)
                );
                const servicesData = await servicesRes.json();
                if (servicesData.success && servicesData.services) {
                  serviceList.push(...servicesData.services.slice(0, 2).map((s: any) => ({
                    id: s.id,
                    name: s.name,
                    description: s.description,
                    price: s.price,
                    image: s.image || '/default-service.jpg'
                  })));
                }
              }
            }
          } catch (err) {
            console.warn('Failed to fetch services for location:', err);
            // Still show the location page even if services fail
          }
          
          setServices(serviceList);
        } else {
          setError('Failed to load location data');
        }
      } catch (err) {
        console.error('Error fetching location data:', err);
        setError('Unable to load location. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchLocationAndServices();
  }, [location]);

  if (loading) {
    return (
      <Layout>
        <div className="min-h-screen flex items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin" />
        </div>
      </Layout>
    );
  }

  if (error || !locationInfo) {
    return (
      <Layout>
        <div className="min-h-screen flex items-center justify-center">
          <Card className="w-full max-w-md">
            <CardContent className="pt-6">
              <div className="flex gap-2 items-center mb-4">
                <AlertCircle className="h-5 w-5 text-red-600" />
                <p className="text-center text-gray-600">{error || 'Location not found'}</p>
              </div>
              <Button className="w-full" onClick={() => navigate('/services')}>
                Browse All Services
              </Button>
            </CardContent>
          </Card>
        </div>
      </Layout>
    );
  }

  const pageTitle = `Property Services in ${locationInfo.full_name} | Virewire`;
  const pageDescription = locationInfo.description;
  const url = `https://virewire.com/${location}`;

  return (
    <Layout>
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={pageDescription} />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={pageDescription} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={url} />
      </Helmet>

      {/* JSON-LD Schemas */}
      <JsonLd schema={getLocalBusinessSchema(locationInfo.full_name, locationInfo.latitude, locationInfo.longitude)} />
      
      {services.map(service => (
        <JsonLd 
          key={service.id}
          schema={getServiceSchema({
            name: service.name,
            description: service.description,
            price: service.price.replace('From $', ''),
            priceCurrency: 'GBP',
            areaServed: [locationInfo.full_name],
            provider: 'Virewire'
          })}
        />
      ))}

      <JsonLd schema={getBreadcrumbSchema([
        { name: 'Home', url: 'https://virewire.com' },
        { name: 'Services', url: 'https://virewire.com/services' },
        { name: locationInfo.full_name, url }
      ])} />

      <div className="min-h-screen bg-gradient-to-b from-blue-50 to-white">
        {/* Hero Section */}
        <div className="bg-gradient-to-r from-blue-600 to-blue-800 text-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
            <div className="flex items-center gap-3 mb-4">
              <MapPin className="w-6 h-6" />
              <span className="text-blue-100">Property Services</span>
            </div>
            <h1 className="text-4xl md:text-5xl font-bold mb-4">
              Professional Services in {locationInfo.full_name}
            </h1>
            <p className="text-xl text-blue-100 max-w-2xl mb-8">
              {locationInfo.service_area_description}
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <a href="/dashboard/request-service">
                <Button size="lg" variant="default">
                  Get a Free Quote
                </Button>
              </a>
              <a href="/contact">
                <Button size="lg" variant="outline" className="bg-white text-blue-600 border-white hover:bg-blue-50">
                  Contact Us
                </Button>
              </a>
            </div>
          </div>
        </div>

        {/* Why Choose Us */}
        <section className="py-16 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <SectionHeader
              title="Why Choose Virewire"
              subtitle={`Local expertise serving ${locationInfo.full_name} with professional property solutions`}
            />
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mt-12">
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Local Expertise</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-600">
                    Our {locationInfo.name}-based team has deep knowledge of local properties and requirements.
                  </p>
                </CardContent>
              </Card>
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Competitive Pricing</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-600">
                    Transparent, region-specific pricing that provides value without hidden costs.
                  </p>
                </CardContent>
              </Card>
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Reliable Service</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-600">
                    Professional, vetted providers ensuring quality work every time.
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        {/* Services Section */}
        {!loading && services.length > 0 && (
          <section className="py-16 bg-gray-50">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <SectionHeader
                title={`Our Services in ${locationInfo.name}`}
                subtitle="Comprehensive property solutions tailored to your needs"
              />
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mt-12">
                {services.map(service => (
                  <Card key={service.id} className="hover:shadow-lg transition-shadow">
                    <div className="aspect-square bg-gray-200 overflow-hidden rounded-t-lg">
                      <img 
                        src={service.image} 
                        alt={service.name}
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <CardContent className="pt-4">
                      <h3 className="font-semibold text-gray-900 mb-2">{service.name}</h3>
                      <p className="text-sm text-gray-600 mb-4 line-clamp-2">{service.description}</p>
                      <p className="text-lg font-bold text-blue-600 mb-4">{service.price}</p>
                      <a href={`/service/${service.id}`}>
                        <Button variant="outline" className="w-full">
                          Learn More
                        </Button>
                      </a>
                    </CardContent>
                  </Card>
                ))}
              </div>
              <div className="text-center mt-12">
                <a href="/services">
                  <Button size="lg">View All Services</Button>
                </a>
              </div>
            </div>
          </section>
        )}

        {/* Loading State */}
        {loading && (
          <section className="py-16 bg-gray-50">
            <div className="flex justify-center items-center min-h-80">
              <Loader2 className="w-12 h-12 animate-spin text-blue-600" />
            </div>
          </section>
        )}

        {/* FAQ Section */}
        <section className="py-16 bg-white">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-gray-900 mb-12 text-center">
              Frequently Asked Questions
            </h2>
            <div className="space-y-4">
              <Card>
                <CardHeader className="cursor-pointer hover:bg-gray-50">
                  <CardTitle className="text-lg">Do you service {locationInfo.full_name}?</CardTitle>
                </CardHeader>
                <CardContent className="text-gray-600">
                  Yes, we provide comprehensive property services throughout {locationInfo.full_name} and surrounding areas. Contact us for specific location details.
                </CardContent>
              </Card>
              <Card>
                <CardHeader className="cursor-pointer hover:bg-gray-50">
                  <CardTitle className="text-lg">What are your service hours?</CardTitle>
                </CardHeader>
                <CardContent className="text-gray-600">
                  We operate Monday to Friday, 8am-6pm, with weekend services available for emergencies. Contact us to arrange a time that suits you.
                </CardContent>
              </Card>
              <Card>
                <CardHeader className="cursor-pointer hover:bg-gray-50">
                  <CardTitle className="text-lg">How quickly can you respond?</CardTitle>
                </CardHeader>
                <CardContent className="text-gray-600">
                  Most requests are scheduled within 48 hours. For urgent needs, we offer expedited booking.
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-16 bg-blue-600 text-white">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-3xl font-bold mb-4">Ready to get started?</h2>
            <p className="text-xl text-blue-100 mb-8">
              Get a free, no-obligation quote for your {locationInfo.name} property today.
            </p>
            <a href="/dashboard/request-service">
              <Button size="lg" variant="outline" className="bg-white text-blue-600 hover:bg-blue-50">
                Request a Quote
              </Button>
            </a>
          </div>
        </section>
      </div>
    </Layout>
  );
}
